import {
  AccountCentralizedInfo,
  AccountInfoResult,
  ContractDataRawResult,
  CsprNameInfoResult,
  DataResponse,
  DeployContractPackageResult,
  getAccountInfoLogo,
  getCentralizedAccountInfoLogo,
} from '@block-explorer/entities';

import { findHash, isValidCsprName } from '../utils/account';
import { getHashWithoutPrefix } from '../utils/hash';

export const hashPrefixRegEx = new RegExp(
  `(?:(entity-)?contract(?:-package)?|account-hash|dictionary|withdraw|balance|deploy|uref|hash|era|bid)-(?=[0-9a-fA-F])`,
  'gi'
);

export const MIN_NAME_SEARCH_LENGTH = 3;
export const MAX_NAME_SEARCH_LENGTH = 256;

type AccountPublicKeySearchResult = {
  data?: {
    account_hash?: string | null;
    public_key?: string | null;
  } | null;
} | null;

type CsprNameInfoSearchResult = {
  data: CsprNameInfoResult;
} | null;

export type SearchByNameResults = {
  contractPackages: DeployContractPackageResult[] | null;
  verifiedAccounts: AccountInfoResult[] | null;
  centralizedAccounts: DataResponse<AccountCentralizedInfo[]> | null;
};

type ResolveSearchResultParams = {
  searchTerm: string;
  isValidAccount: boolean;
  csprNameInfo: CsprNameInfoSearchResult;
  blockData: unknown | null;
  deployData: unknown | null;
  accountPublicKey: AccountPublicKeySearchResult;
  stateItemData: ContractDataRawResult | null;
  searchByNameResults: SearchByNameResults | null;
  blockByHeight: any | null;
};

export type SearchResult = {
  type:
    | 'block'
    | 'deploy'
    | 'transaction'
    | 'account'
    | 'contract'
    | 'contract-package'
    | 'uref'
    | 'named-search';
  hash?: string;
  results?: SearchByNameResults | null;
};

export type AccountSearchResult = {
  key: string;
  accountHash: string;
  publicKey: string | null;
  name?: string;
  url?: string;
  logo: string | null;
  isValidator: boolean;
  isKnownOwner: boolean;
  isVerified: boolean;
};

export const isBlockHeightTerm = (value?: string | null): boolean => {
  if (!value) return false;

  const normalized = value.replace(/,/g, '').trim();

  return /^\d+$/.test(normalized);
};

export const canSearchByNameTerm = (
  searchTerm?: string | null
): searchTerm is string => {
  const trimmedTerm = searchTerm?.trim();
  const searchTermLength = trimmedTerm?.length;

  return (
    trimmedTerm != null &&
    searchTermLength != null &&
    searchTermLength >= MIN_NAME_SEARCH_LENGTH &&
    searchTermLength <= MAX_NAME_SEARCH_LENGTH &&
    !isBlockHeightTerm(trimmedTerm) &&
    !isValidCsprName(trimmedTerm) &&
    !findHash(trimmedTerm)
  );
};

export const resolveSearchResult = ({
  searchTerm,
  isValidAccount,
  csprNameInfo,
  blockData,
  deployData,
  accountPublicKey,
  stateItemData,
  searchByNameResults,
  blockByHeight,
}: ResolveSearchResultParams): SearchResult | null => {
  if (csprNameInfo != null) {
    return {
      type: 'account',
      hash:
        csprNameInfo.data.resolved_public_key ||
        csprNameInfo.data.resolved_hash,
    };
  }

  if (blockData != null) {
    return { type: 'block', hash: searchTerm };
  }

  if (blockByHeight != null) {
    return { type: 'block', hash: blockByHeight.data.block_hash };
  }

  if (deployData != null) {
    return { type: 'transaction', hash: searchTerm };
  }

  if (accountPublicKey?.data?.public_key) {
    return {
      type: 'account',
      hash: accountPublicKey.data.public_key,
    };
  }

  if (stateItemData?.Contract != null) {
    return {
      type: 'contract',
      hash: getHashWithoutPrefix(searchTerm),
    };
  }

  if (stateItemData?.ContractPackage != null) {
    return {
      type: 'contract-package',
      hash: getHashWithoutPrefix(searchTerm),
    };
  }

  if (accountPublicKey?.data?.account_hash) {
    return {
      type: 'account',
      hash: accountPublicKey.data.account_hash,
    };
  }

  if (stateItemData?.Account != null) {
    return {
      type: 'account',
      hash: getHashWithoutPrefix(searchTerm),
    };
  }

  if (stateItemData != null) {
    return { type: 'uref', hash: searchTerm };
  }

  if (isValidAccount) {
    return { type: 'account', hash: searchTerm };
  }

  if (
    searchByNameResults != null &&
    ((searchByNameResults.centralizedAccounts?.data?.length ?? 0) > 0 ||
      (searchByNameResults.verifiedAccounts?.length ?? 0) > 0 ||
      (searchByNameResults.contractPackages?.length ?? 0) > 0)
  ) {
    return { type: 'named-search', results: searchByNameResults };
  }

  return null;
};

export const getAccountSearchResults = (
  verifiedAccounts: AccountInfoResult[],
  centralizedAccounts: AccountCentralizedInfo[],
  getAccountInfoByHash: (accountHash: string) => AccountInfoResult | undefined
): AccountSearchResult[] => {
  const mappedCentralizedAccounts = centralizedAccounts.map(
    (centralizedAccount) => ({
      key: centralizedAccount.account_hash,
      accountHash: centralizedAccount.account_hash,
      publicKey:
        getAccountInfoByHash(centralizedAccount.account_hash)?.public_key ||
        null,
      name: centralizedAccount.name,
      url: centralizedAccount.url,
      logo: getCentralizedAccountInfoLogo(centralizedAccount),
      isValidator: false,
      isVerified: false,
      isKnownOwner: true,
    })
  );

  const mappedVerifiedAccounts = verifiedAccounts.map((verifiedAccount) => ({
    key: verifiedAccount.account_hash,
    accountHash: verifiedAccount.account_hash,
    publicKey:
      verifiedAccount.public_key ||
      getAccountInfoByHash(verifiedAccount.account_hash)?.public_key ||
      null,
    name: verifiedAccount.info?.owner?.name,
    url: verifiedAccount.url || verifiedAccount.info?.owner?.website,
    logo: getAccountInfoLogo(verifiedAccount),
    isValidator: getAccountInfoByHash(verifiedAccount.account_hash)
      ?.auction_status,
    isKnownOwner: false,
    isVerified: true,
  }));

  const accountsByHash = [
    ...mappedCentralizedAccounts,
    ...mappedVerifiedAccounts,
  ].reduce((result, account) => {
    result[account.accountHash] = account;
    return result;
  }, {});

  return Object.values(accountsByHash);
};
